import React from "react";
import { Button } from "@truevault/aquilo";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/20/solid";
import clsx from "clsx";

export interface AlertCardProps {
  variant?: "info" | "success" | "warning" | "error" | "general";
  title?: string;
  description?: string | React.ReactNode;
  buttonText?: string;
  onButtonClick?: () => void;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  showDefaultIcon?: boolean;
  containerClasses?: string;
}

const getVariantClasses = (variant: string) => {
  switch (variant) {
    case "info":
      return {
        container: "bg-blue-50",
        text: "text-blue-600",
        icon: "text-blue-400",
      };
    case "success":
      return {
        container: "bg-green-100",
        text: "text-green-700",
        icon: "text-green-400",
      };
    case "warning":
      return {
        container: "bg-yellow-100",
        text: "text-yellow-700",
        icon: "text-yellow-400",
      };
    case "error":
      return {
        container: "bg-red-50",
        text: "text-red-500",
        icon: "text-red-400",
      };
    case "general":
      return {
        container: "bg-neutral-100",
        text: "text-neutral-800",
        icon: "text-neutral-500",
      };
    default:
      return {
        container: "bg-blue-50",
        text: "text-blue-600",
        icon: "text-blue-400",
      };
  }
};

const getDefaultIcon = (variant: string) => {
  switch (variant) {
    case "info":
      return <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />;
    case "success":
      return <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />;
    case "warning":
      return <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />;
    case "error":
      return <XCircleIcon className="w-5 h-5 flex-shrink-0" />;
    case "general":
      return <DocumentTextIcon className="w-5 h-5 flex-shrink-0" />;
    default:
      return <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />;
  }
};

const AlertCard = ({
  variant = "info",
  title,
  description,
  buttonText,
  onButtonClick,
  children,
  icon,
  showDefaultIcon = true,
  containerClasses,
}: AlertCardProps) => {
  const variantClasses = getVariantClasses(variant);
  const displayIcon = icon || (showDefaultIcon ? getDefaultIcon(variant) : null);

  return (
    <div
      className={clsx(
        "tw-boundary",
        "mt-6",
        "mb-6",
        "flex",
        "items-start",
        "text-sm",
        "gap-3",
        "p-4",
        "text-left",
        "rounded-md",
        variantClasses.container,
        containerClasses,
      )}
    >
      {displayIcon && <div className={variantClasses.icon}>{displayIcon}</div>}
      <div className="flex-1">
        {title && <div className={`font-semibold mb-1 ${variantClasses.text}`}>{title}</div>}
        {description && <p className={`m-0 leading-5 ${variantClasses.text}`}>{description}</p>}
        {children && <div className={variantClasses.text}>{children}</div>}
        {buttonText && onButtonClick && (
          <div className="mt-3">
            <Button size="s" color="tertiary" onClick={onButtonClick}>
              {buttonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertCard;
